import { useMutation, useQueryClient } from '@tanstack/react-query';
import LocationService, {
  IUpdateLocationParams,
} from '@clickersphere/core/services/LocationService';
import { EventQueryData, EVENTS_QUERY_KEY } from '../event/useEvents';
import { LeadQueryData, LEADS_QUERY_KEY } from '../lead/useLeads';
import { useBuildQueryKey } from '../useBuildQueryKey';
import { LOCATIONS_QUERY_KEY, LocationQueryData } from './useLocations';

export function useUpdateLocation() {
  const queryClient = useQueryClient();
  const { buildQueryKey } = useBuildQueryKey();

  const {
    isPending: updateLocationIsPending,
    mutateAsync: updateLocationAsync,
  } = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: IUpdateLocationParams;
    }) => LocationService.update(id, { ...data }),
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(LOCATIONS_QUERY_KEY),
      });
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(EVENTS_QUERY_KEY),
      });
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(LEADS_QUERY_KEY),
      });

      const oldLocations = queryClient.getQueryData<LocationQueryData[]>(
        buildQueryKey(LOCATIONS_QUERY_KEY),
      );
      const oldEvents = queryClient.getQueryData<EventQueryData[]>(
        buildQueryKey(EVENTS_QUERY_KEY),
      );
      const oldLeadsQueries = queryClient.getQueriesData<unknown>({
        queryKey: buildQueryKey(LEADS_QUERY_KEY),
      });

      queryClient.setQueryData<LocationQueryData[]>(
        buildQueryKey(LOCATIONS_QUERY_KEY),
        (old) =>
          old?.map((location) =>
            location.id === variables.id
              ? { ...location, ...variables.data, queryStatus: 'pending' }
              : location,
          ),
      );

      if (variables.data.name) {
        const renameEvent = (event: EventQueryData) =>
          event.locationId === variables.id
            ? { ...event, location: variables.data.name }
            : event;

        queryClient.setQueryData<EventQueryData[]>(
          buildQueryKey(EVENTS_QUERY_KEY),
          (old) => old?.map(renameEvent),
        );

        oldLeadsQueries.forEach(([queryKey, data]) => {
          if (Array.isArray(data)) {
            queryClient.setQueryData(
              queryKey,
              (data as LeadQueryData[]).map((lead) => ({
                ...lead,
                events: (lead.events ?? []).map(renameEvent),
              })),
            );
          } else if (data && typeof data === 'object') {
            const lead = data as LeadQueryData;
            queryClient.setQueryData(queryKey, {
              ...lead,
              events: (lead.events ?? []).map(renameEvent),
            });
          }
        });
      }

      return { oldLocations, oldEvents, oldLeadsQueries };
    },

    onSuccess: async (_data, variables) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(LOCATIONS_QUERY_KEY),
      });

      queryClient.setQueryData<LocationQueryData[]>(
        buildQueryKey(LOCATIONS_QUERY_KEY),
        (old) =>
          old?.map((location) =>
            location.id === variables.id
              ? { ...location, queryStatus: undefined }
              : location,
          ),
      );
    },

    onError: async (_error, _variables, context) => {
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(LOCATIONS_QUERY_KEY),
      });
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(EVENTS_QUERY_KEY),
      });
      await queryClient.cancelQueries({
        queryKey: buildQueryKey(LEADS_QUERY_KEY),
      });

      queryClient.setQueryData(
        buildQueryKey(LOCATIONS_QUERY_KEY),
        context?.oldLocations,
      );
      queryClient.setQueryData(
        buildQueryKey(EVENTS_QUERY_KEY),
        context?.oldEvents,
      );
      context?.oldLeadsQueries.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
  });

  return {
    updateLocationAsync,
    updateLocationIsPending,
  };
}
